import { Link } from 'react-router-dom';
import {
  Monitor,
  Users,
  Mic,
  Radio,
  Wrench,
  Building,
  BookOpen,
  ArrowRight
} from 'lucide-react';

const ServicesPage = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20 relative overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="hero-glow top-0 left-1/4" />
        
        <div className="container-main relative z-10">
          <div className="max-w-3xl">
            <p className="font-mono text-[var(--primary)] text-sm uppercase tracking-wider mb-4">
              ./our_services
            </p>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter text-[var(--text-main)] mb-6">
              Complete Cybersecurity
              <br />
              <span className="text-[var(--primary)]">Training Suite</span>
            </h1>
            <p className="text-[var(--text-muted)] text-lg md:text-xl leading-relaxed">
              From quarterly training events to intensive cohorts, we offer multiple pathways 
              to help you build and advance your cybersecurity career.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16" data-testid="services-grid">
        <div className="container-main">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Quarterly Trainings */}
            <Link
              to="/services/quarterly-trainings"
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary)] transition-colors duration-300 p-8 group"
              data-testid="service-card-quarterly-trainings"
            >
              <div className="w-14 h-14 bg-[var(--primary)]/10 border border-[var(--primary)] flex items-center justify-center mb-6">
                <Monitor className="w-7 h-7 text-[var(--primary)]" />
              </div>
              <span className="text-xs font-mono text-[var(--primary)] uppercase tracking-wider">Quarterly Programs</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--primary)] transition-colors duration-200">
                Quarterly Trainings
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Intensive instructor-led training programs hosted quarterly, available online or at designated venues. Each program includes hands-on labs, real-world scenarios, and expert mentorship.
              </p>
              <div className="flex items-center gap-2 text-[var(--primary)] font-mono text-sm">
                <span>View Upcoming Events</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Corporate Trainings */}
            <Link
              to="/services/corporate"
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-purple)] transition-colors duration-300 p-8 group"
              data-testid="service-card-corporate"
            >
              <div className="w-14 h-14 bg-[var(--accent-purple)]/10 border border-[var(--accent-purple)] flex items-center justify-center mb-6">
                <Building className="w-7 h-7 text-[var(--accent-purple)]" />
              </div>
              <span className="text-xs font-mono text-[var(--accent-purple)] uppercase tracking-wider">Enterprise Solutions</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--accent-purple)] transition-colors duration-200">
                Corporate Trainings
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Customized technical and security awareness training programs for enterprise teams with technical deep dives and compliance modules.
              </p>
              <div className="flex items-center gap-2 text-[var(--accent-purple)] font-mono text-sm">
                <span>Get Quote</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Co-horts */}
            <Link 
              to="/services/cohorts" 
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-cyan)] transition-colors duration-300 p-8 group"
              data-testid="service-card-cohorts"
            >
              <div className="w-14 h-14 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)] flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-[var(--accent-cyan)]" />
              </div>
              <span className="text-xs font-mono text-[var(--accent-cyan)] uppercase tracking-wider">Intensive Programs</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--accent-cyan)] transition-colors duration-200">
                Co-horts
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                12-week intensive cohort programs with live instruction, peer collaboration, mentorship, and job placement support.
              </p>
              <div className="flex items-center gap-2 text-[var(--accent-cyan)] font-mono text-sm">
                <span>Browse Programs</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Byt3cast Podcast */}
            <Link 
              to="/services/podcast" 
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-purple)] transition-colors duration-300 p-8 group"
              data-testid="service-card-podcast"
            >
              <div className="w-14 h-14 bg-[var(--accent-purple)]/10 border border-[var(--accent-purple)] flex items-center justify-center mb-6">
                <Mic className="w-7 h-7 text-[var(--accent-purple)]" />
              </div>
              <span className="text-xs font-mono text-[var(--accent-purple)] uppercase tracking-wider">Video Podcast</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--accent-purple)] transition-colors duration-200">
                Byt3cast Podcast
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Weekly video podcast featuring interviews with security researchers, ethical hackers, and industry leaders on YouTube.
              </p>
              <div className="flex items-center gap-2 text-[var(--accent-purple)] font-mono text-sm">
                <span>Watch Episodes</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Live Sessions */}
            <Link 
              to="/services/live-sessions" 
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--primary)] transition-colors duration-300 p-8 group"
              data-testid="service-card-live-sessions"
            >
              <div className="w-14 h-14 bg-[var(--primary)]/10 border border-[var(--primary)] flex items-center justify-center mb-6">
                <Radio className="w-7 h-7 text-[var(--primary)]" />
              </div>
              <span className="text-xs font-mono text-[var(--primary)] uppercase tracking-wider">Interactive Learning</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--primary)] transition-colors duration-200">
                Live Sessions
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Interactive live workshops, Q&A sessions, CTF nights, and tool demos you can register for and participate in real-time.
              </p>
              <div className="flex items-center gap-2 text-[var(--primary)] font-mono text-sm">
                <span>View Schedule</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Tool Demos */}
            <Link
              to="/services/tool-demos"
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-cyan)] transition-colors duration-300 p-8 group"
              data-testid="service-card-tool-demos"
            >
              <div className="w-14 h-14 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)] flex items-center justify-center mb-6">
                <Wrench className="w-7 h-7 text-[var(--accent-cyan)]" />
              </div>
              <span className="text-xs font-mono text-[var(--accent-cyan)] uppercase tracking-wider">Master Your Toolkit</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--accent-cyan)] transition-colors duration-200">
                CyberSecurity Tool Demos
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Deep-dive demonstrations of 75+ security tools used by professionals, with setup guides and real-world usage examples.
              </p>
              <div className="flex items-center gap-2 text-[var(--accent-cyan)] font-mono text-sm">
                <span>Explore Tools</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

            {/* Study Groups */}
            <Link
              to="/services/study-groups"
              className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--accent-cyan)] transition-colors duration-300 p-8 group"
              data-testid="service-card-study-groups"
            >
              <div className="w-14 h-14 bg-[var(--accent-cyan)]/10 border border-[var(--accent-cyan)] flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7 text-[var(--accent-cyan)]" />
              </div>
              <span className="text-xs font-mono text-[var(--accent-cyan)] uppercase tracking-wider">Community Learning</span>
              <h3 className="text-2xl font-bold text-[var(--text-main)] mt-2 mb-4 group-hover:text-[var(--accent-cyan)] transition-colors duration-200">
                Study Groups
              </h3>
              <p className="text-[var(--text-muted)] leading-relaxed mb-6">
                Community-driven study groups for focused skill development in specific cybersecurity domains, led by mentors with peer collaboration.
              </p>
              <div className="flex items-center gap-2 text-[var(--accent-cyan)] font-mono text-sm">
                <span>Join a Group</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </div>
            </Link>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20  border-t border-[var(--border)] relative overflow-hidden" data-testid="services-cta">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="hero-glow bottom-0 right-0" />
        
        <div className="container-main relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[var(--text-main)] mb-6">
            Not Sure Where to Start?
          </h2>
          <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-10">
            Our team can help you choose the right program based on your experience level, 
            goals, and schedule. Let's find your path.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[var(--primary)] text-black font-mono font-bold px-10 py-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_20px_var(--glow-primary)]"
            data-testid="services-contact-cta"
          >
            Schedule a Consultation <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
